import React, { useState, useEffect, useRef } from 'react';
import { Book, ChevronDown } from 'lucide-react';
import { bibleVersions } from '../lib/bibleVersions';

interface VersionSelectorProps {
  selectedVersion: string;
  onSelectVersion: (version: string) => void;
}

export function VersionSelector({ selectedVersion, onSelectVersion }: VersionSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const availableVersions = bibleVersions.filter(v => v.available);
  const currentVersion = availableVersions.find(v => v.id === selectedVersion) || availableVersions[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleVersionSelect = async (versionId: string) => {
    onSelectVersion(versionId);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-4 py-2 text-white bg-white/10 rounded-lg hover:bg-white/20 transition-colors flex items-center gap-2"
      >
        <Book size={18} />
        <span>{currentVersion?.name || 'Select Version'}</span>
        <ChevronDown size={16} className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-[min(320px,calc(100vw-2rem))] bg-slate-800 rounded-lg shadow-xl border border-white/10 left-0">
          <div className="p-2 space-y-1">
            {availableVersions.map((version) => (
              <div key={version.id} className="flex items-center gap-2">
                <button
                  onClick={() => handleVersionSelect(version.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    selectedVersion === version.id
                      ? 'bg-gold-400/20 text-gold-400'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-sm truncate">
                      {version.name} - {version.fullName}
                    </span>
                    <span className="text-xs text-white/60 truncate">
                      {version.year ? `Published ${version.year} • ` : ''}
                      {version.license}
                    </span>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}